import * as React from 'react';
import { useParams } from 'react-router';
import { Page } from '../../subs/Page';
import { Alert, Button, Collapse, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { AlertProps } from '@mui/material/Alert';
import { Link as LinkRouter } from 'react-router-dom';

import AddBoxIcon from '@mui/icons-material/AddBox';

import { getQueryRunner, httpGet } from '../../Web';
import { WorkshopUser, WorkshopUserStatus, getWorkshopUsers } from '../../types/WorkshopUser';

import CheckBoxIcon from '@mui/icons-material/CheckBox';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import PlaylistAddCheckCircleIcon from '@mui/icons-material/PlaylistAddCheckCircle';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { WorkshopInterface } from '../../types/WorkshopInterface';
import { Interface } from '../../types/Interface';
import { WorkshopProduct } from '../../types/WorkshopProducts';
import { Product } from '../../types/Product';
import { IErrorInfo } from '../../ErrorAlert';
import { matchLeft, unique } from '../../Utils';
import { PageSection } from '../../subs/PageSection';
//import { useLocation } from 'react-router-dom';
import { FeatureNotAvailableIcon } from '../../subs/FeatureNotAvailableIcon';
import { SuccessFailureIndicator } from '../../subs/SuccessFailureIndicator';
import { RemoveWorkshopProductModal } from '../../modals/RemoveWorkshopProduct';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { UpdateWsProductForm2, AddProductToWorkshopLocalModal } from '../../modals/AddProductToWorkshopModal';
import { WorkshopProductInterface } from '../../types/WorkshopProductInterface';
import { Workshop, WorkshopStatus, getWorkshop } from '../../types/Workshop';
import { UserContext } from '../../UserProvider';
import { getUserWorkshops, isGlobalAdmin, getUser } from '../../types/User';
import { useSnackbar } from '../../../contexts/SnackBarContext';


export interface UserProductContextType {
    workshopId: string,
    workshopUsers: WorkshopUser[],
    interfaces: Interface[],
    workshopInterfaces: WorkshopInterface[],
    productInterfaces: WorkshopProductInterface[],
    setProductInterfaces: (value: React.SetStateAction<WorkshopProductInterface[]>) => void,
    productsWorkshopProducts: Array<[Product, Array<WorkshopProduct>]>,
    workshopProducts: WorkshopProduct[],
    setWorkshopProducts: (value: React.SetStateAction<WorkshopProduct[]>) => void
}

export const UserProductContext = 
    React.createContext<UserProductContextType>({workshopId: "", workshopUsers: [], interfaces: [],
                                                 workshopInterfaces: [], productInterfaces: [], productsWorkshopProducts: [],
                                                 setProductInterfaces: (value) =>{}, workshopProducts: [], setWorkshopProducts: (value)=>{}});

const PageHeader = ({ workshopId, isWSUser, workshopProducts, setWorkshopProducts, productInterfaces, setProductInterfaces }:
    {
        workshopId: string, isWSUser: boolean,
        workshopProducts: WorkshopProduct[],
        setWorkshopProducts: React.Dispatch<React.SetStateAction<WorkshopProduct[]>>,
        productInterfaces: WorkshopProductInterface[],
        setProductInterfaces: React.Dispatch<React.SetStateAction<WorkshopProductInterface[]>>,
    } ) => {
   // const pageLocation = useLocation();
    const [addModalOpenState, setAddModalOpenState] = React.useState<boolean>(false);

    return <>
        <AddProductToWorkshopLocalModal workshopID={Number(workshopId)} workshopProducts={workshopProducts}
            setWorkshopProducts={setWorkshopProducts} openState={addModalOpenState} setOpenState={setAddModalOpenState} 
            workshopProductsInterfaces={productInterfaces} setWorkshopProductInterfaces={setProductInterfaces} />

            <Stack direction={'row'} justifyContent={'space-between'}>
                <Typography variant='h4'>{'List of registered users and Products'}</Typography>
            <Stack direction={'row'} columnGap={5}>
                <Button disabled={!isWSUser} startIcon={<AddBoxIcon />} 
                    onClick={() => { setAddModalOpenState(true); }}>
                    {'Add Product'}
                </Button>
                </Stack>
           </Stack></>
}

const WsProductInterfaceTable = ({ workshopUser, products, workshopData, workshopProducts, setWorkshopProducts,
                                interfaces, productInterfaces, setProductInterfaces }:
                                 {workshopUser: WorkshopUser, products: Product[], workshopProducts: WorkshopProduct[],
                                    setWorkshopProducts: React.Dispatch<React.SetStateAction<WorkshopProduct[]>>,
                                    workshopData: Workshop | undefined, 
                                  interfaces: Interface[], productInterfaces: WorkshopProductInterface[],
                                  setProductInterfaces: React.Dispatch<React.SetStateAction<WorkshopProductInterface[]>>,
                                 }) => {                 
    const { showSnackbar } = useSnackbar();

    const [wpIdselected, setWpIdSelected] = React.useState<number>(-1);
    const [deleteModalOpenState, setDeleteModalOpenState] = React.useState<boolean>(false);
    const [editWorkshopProductModalOpenState, setEditWorkshopProductModalOpenState] = React.useState<boolean>(false);
    const userContext = React.useContext(UserContext);
    const [userProducts, setUserProducts] = React.useState<[Product, WorkshopProduct][]>([]);
    const [userRights, setuserRights] = React.useState<number>(0);
   // const [workshopData, setWorkshopData] = React.useState<Workshop>();
    
    const interfaces2productInterfaces: [Interface, WorkshopProductInterface[]][] = matchLeft(interfaces, productInterfaces, (iface, pif) => iface.id === pif.interfaceId);

    React.useEffect(() => {
        setUserProducts(matchLeft(products, workshopProducts, (left: Product, right: WorkshopProduct) => left.id === right.productId)
            .filter(([product, workshopProducts]) => product.companyId === workshopUser.companyId)
            .flatMap(([product, workshopProducts]) => workshopProducts.map(wp => [product, wp] as [Product, WorkshopProduct])));
        
    }, [workshopProducts, products, productInterfaces, workshopUser]);

    const showUserFeedback = React.useCallback((msg: string, severity: AlertProps['severity'] = 'error') => {
        showSnackbar(msg, severity);
    }, [showSnackbar]);

    React.useEffect(() => {
        const fetch = async () => {
            try {
                const userContextData = await getUser(userContext.user.id!!, new AbortController());

                if (userContextData) {
                    setuserRights(userContextData.result.accessLevel ? userContextData.result.accessLevel : 0);
                    return;
                }
            } catch (error) {
                showUserFeedback(String(error), "error");
            }
        }
        fetch();
    }, [userContext, showUserFeedback])

    const columns: GridColDef[] = React.useMemo(() => {
        const base: GridColDef[] = [
            {
                field: 'productName',
                headerName: 'Product Name',
                valueGetter: (_value, row) => row[0].name,
                flex: 2,
                minWidth: 300, // keep a sensible minimum so this column doesn't shrink too far
            },
        ];

        const ifaceCols = interfaces.map((callbackFnxxy, index) => {
            return {
                field: callbackFnxxy.shortName,
                headerName: callbackFnxxy.shortName,
                renderCell: (params: GridRenderCellParams<[Product, WorkshopProduct]>) => params.value ? <CheckBoxIcon /> : <FeatureNotAvailableIcon />,
                valueGetter: (_value: any, row: [Product, WorkshopProduct]) => { return interfaces2productInterfaces[index][1].find(prodIfaces => prodIfaces.workshopProductId === row[1].id); },
                flex: 1,
                minWidth: 150, // ensures column keeps a minimum width
            } as GridColDef;
        });

        const actions: GridColDef = {
            field: 'actionButtons',
            type: 'actions',
            flex: 1,
            minWidth: 180, // avoid action buttons collapsing too small
            renderCell: (params: any) => ([
                <Tooltip title={'This allows specifying the supported OPC UA features in more detail and provide additional information about the product like EndpointUrls or username/password combinations.'}>
                    <IconButton aria-label="Configure" disabled={workshopData?.status === WorkshopStatus.Registration ? false : workshopData?.status === WorkshopStatus.Testing ? false : true}
                        component={LinkRouter} to={`/WSTesting/${params.row[1].workshopId}`} >
                        <SettingsIcon />
                    </IconButton>
                </Tooltip>,
                <Tooltip title={'This opens the testing screen to allow entering testing results for the event.'}>
                    <IconButton aria-label="Start Testing" disabled={workshopData?.status === WorkshopStatus.Testing ? false : true}
                        component={LinkRouter} to={`/wstestingrecords/${params.row[1].workshopId}`} >
                        <PlaylistAddCheckCircleIcon />
                    </IconButton>
                </Tooltip>,
                <Tooltip title={'Edit the product definitions like product name and supported interfaces like OPC UA Client/Server, PubSub, UAFX and others.'}>
                    <IconButton aria-label="Edit Product" disabled={userRights === 0 ? true : false}
                        onClick={() => {
                            setWpIdSelected(params.row[1].id); setEditWorkshopProductModalOpenState(true);
                        }} >
                        <EditIcon />
                    </IconButton>
                </Tooltip>,
                <Tooltip title={'Delete this Product and all of its settings from the Workshop.'}>
                    <IconButton aria-label="Delete Product" disabled={userRights === 0 ? true : false}
                        onClick={() => {
                            setWpIdSelected(params.row[1].id); setDeleteModalOpenState(true);
                        }}>
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            ]
            ),
        };

        return [...base, ...ifaceCols, actions];
    }, [interfaces, interfaces2productInterfaces, userRights, workshopData]);

    // compute a practical minimum width for the whole grid from column minWidths so the grid will overflow and show a horizontal scrollbar
    const gridMinWidth = React.useMemo(() => columns.reduce((acc, c) => acc + (c.minWidth ?? 0), 0), [columns]);

    return <>
        <RemoveWorkshopProductModal setWorkshopProducts={setWorkshopProducts} workshopProducts={workshopProducts} openState={deleteModalOpenState}
                                    setOpenState={(state) => setDeleteModalOpenState(state)} wpId={wpIdselected}/>

        <UpdateWsProductForm2 wpId={wpIdselected} workshopProducts={workshopProducts} setWorkshopProducts={setWorkshopProducts} 
                              openState={editWorkshopProductModalOpenState} setOpenState={setEditWorkshopProductModalOpenState}
                              workshopProductsInterfaces={productInterfaces} setWorkshopProductInterfaces={setProductInterfaces}/>
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', overflowX: 'auto' }}>
         <DataGrid 
            columns = {columns}
            rows = {userProducts}
            getRowId={(row) => row[1].id}

            disableColumnMenu hideFooter
            getRowClassName={(params) => params.indexRelativeToCurrentPage % 2 === 0 ? 'Mui-even' : 'Mui-odd'}

            // enforce the computed minimum width so column minWidths are honored and a scrollbar appears when needed
            sx={{ minWidth: gridMinWidth }}
        /> </div> </> 
}

const UserTableRow = ({ workshopData, workshopUser, index, products, workshopProducts, setWorkshopProducts, interfaces, productInterfaces, setProductInterfaces }:
    {
        workshopData: Workshop | undefined, workshopUser: WorkshopUser, index: number, products: Product[], workshopProducts: WorkshopProduct[],
        setWorkshopProducts: React.Dispatch<React.SetStateAction<WorkshopProduct[]>>,
        interfaces: Interface[], productInterfaces: WorkshopProductInterface[],
        setProductInterfaces: React.Dispatch<React.SetStateAction<WorkshopProductInterface[]>>}) => {
    const [open, setOpen] = React.useState<boolean>(false);
    const [firstName, ...lastName] = workshopUser.name?.split(' ') ?? '';

    return <><TableRow hover={true}><TableCell><Typography variant='bold'>{`${lastName.join(' ')}, ${firstName}`}</Typography></TableCell>
                     <TableCell>{workshopUser.companyName}</TableCell>
                     <TableCell>{<SuccessFailureIndicator success={workshopUser?.status === WorkshopUserStatus.Paid} />}</TableCell>
                     <TableCell>{<SuccessFailureIndicator success={workshopUser?.status!! >= WorkshopUserStatus.Registered} />}</TableCell>
                     <TableCell>{<IconButton onClick={() => setOpen(!open)}>{ !open ? <ExpandMoreIcon /> : <ExpandLessIcon />}</IconButton>}</TableCell>
           </TableRow>
           <TableRow>
            <TableCell style={{paddingBottom: 0, paddingTop: 0}} colSpan={5}>
                <Collapse in={open} timeout={'auto'} unmountOnExit>
                    <WsProductInterfaceTable workshopUser={workshopUser} products={products} workshopProducts={workshopProducts} setWorkshopProducts={setWorkshopProducts}
                        interfaces={interfaces} productInterfaces={productInterfaces} workshopData={workshopData}
                        setProductInterfaces={setProductInterfaces} />
                </Collapse>
            </TableCell>
           </TableRow></>
}

export const ListOfRegisteredUsersAndProducts = () => {
    const {id} = useParams();
    const userContext = React.useContext(UserContext);
    const { showSnackbar } = useSnackbar();

    const [workshopData, setWorkshopData] = React.useState<Workshop>();
    const [workshopUsers, setWorkshopUsers] = React.useState<WorkshopUser[]>([]);
    const [products, setProducts] = React.useState<Product[]>([]);

    const [workshopInterfaces, setWorkshopInterfaces] = React.useState<WorkshopInterface[]>([]);
    const [workshopInterfacesError, setWorkshopInterfacesError] = React.useState<IErrorInfo | null>(null);

    const [workshopProducts, setWorkshopProducts] = React.useState<WorkshopProduct[]>([]);
    const [workshopProductsError, setWorkshopProductsError] = React.useState<IErrorInfo | null>(null);

    const [interfaces, setInterfaces] = React.useState<Interface[]>([]);

    const [productInterfaces, setProductInterfaces] = React.useState<WorkshopProductInterface[]>([]);

    const [singleUser, setSingleUser] = React.useState<WorkshopUser>();

    const [userIsAdmin, setUserIsAdmin] = React.useState<boolean>(false);
    const [isWSUser, setIsWSUser] = React.useState<boolean>(false);

    const showUserFeedback = React.useCallback((msg: string, severity: AlertProps['severity'] = 'error') => {
        showSnackbar(msg, severity);
    }, [showSnackbar]);

    React.useEffect(()=> {
        const fetch = async() => {
            try {
                if (Number(id) > 0) {
                    const workshopDataResponse = await getWorkshop(Number(id), new AbortController());

                    if (workshopDataResponse.failed) {
                        showUserFeedback(workshopDataResponse.errorText!!);
                        return;
                    }

                    setWorkshopData(workshopDataResponse.result);
                }

                const globalAdminResponse = await isGlobalAdmin(userContext.user.id!!, new AbortController());
                
                if(globalAdminResponse) {
                    setUserIsAdmin(true);
                    return;
                }

                const workshopAdminResponse = await getUserWorkshops(userContext.user.id!!, new AbortController());
                
                if(workshopAdminResponse.failed) {
                    showUserFeedback(workshopAdminResponse.errorText!!);
                    return;
                }

                const userIsAdmin = typeof workshopAdminResponse.result.find(wu => wu.isWorkshopAdmin) !== 'undefined'
                const isWSUser = typeof workshopAdminResponse.result.find(wu => wu.workshopId === Number(id)) !== 'undefined'
                setUserIsAdmin(userIsAdmin);
                setIsWSUser(isWSUser);
            } catch (error) {
                showUserFeedback(String(error));
            }
        }
        fetch();
    }, [userContext, id, showUserFeedback])

    React.useEffect(()=> {
        const fetch = async() => {
            try {
                let workshopUsersRequest = await getWorkshopUsers(Number(id), new AbortController());

                if (workshopUsersRequest.failed) {
                    showUserFeedback(workshopUsersRequest.errorText!!);
                    return;
                }

                setWorkshopUsers(workshopUsersRequest.result);
                if(!userIsAdmin) {
                    console.log('userIsAdmin: ', userIsAdmin);
                    setSingleUser(workshopUsersRequest.result.find(wu => wu.userId === userContext.user.id));
                }
            } catch (error) {
                showUserFeedback(String(error));
            }
        }
        fetch();
    }, [userContext, userIsAdmin, id, showUserFeedback]);

    React.useEffect(() => {
        getQueryRunner(`/api/workshop/interface/${id}`, setWorkshopInterfaces, setWorkshopInterfacesError)();
    }, [id]);
    React.useEffect(() => {
        getQueryRunner(`/api/workshopproduct/${id}`, setWorkshopProducts, setWorkshopProductsError)();
    }, [id]);

    React.useEffect(()=> {
        httpGet('/api/interface', new AbortController())
        .then(requestResult => setInterfaces(requestResult.result.filter((iface: Interface) => workshopInterfaces.find(wi => wi.interfaceId === iface.id))));
    }, [workshopInterfaces]);

    React.useEffect(() => {
        function getProducts(workshopProducts: WorkshopProduct[]) {
            let requestResults: Array<Promise<Product>> = []

            workshopProducts.map(wp => requestResults.push(httpGet(`/api/product/get/${wp.productId}`, new AbortController())));

            Promise.all(requestResults).then(requestResult => setProducts(unique(requestResult.map((productResult: any) => productResult.result), (p1, p2)=> p1.id - p2.id)))
        }
        getProducts(workshopProducts);
    }, [workshopProducts]);

    React.useEffect(() => {
        function getProductInterfaces(workshopProducts: WorkshopProduct[]) {
            let requestResults: Array<Promise<WorkshopProductInterface>> = [];

            workshopProducts.map(wp => requestResults.push(httpGet(`/api/workshopproduct/interface/${wp.id}`, new AbortController())));

            Promise.all(requestResults).then(requestResult => setProductInterfaces(requestResult.flatMap((pi: any) => pi.result)))
        }

        getProductInterfaces(workshopProducts);
    }, [workshopProducts]);

    return <Page backButton heading={<PageHeader workshopId={id as string}
        isWSUser={isWSUser || userIsAdmin}
        workshopProducts={workshopProducts}
        setWorkshopProducts={setWorkshopProducts}
        productInterfaces={productInterfaces}
        setProductInterfaces={setProductInterfaces}/>}>
        {workshopInterfacesError?.failed && <Alert severity="error">{workshopInterfacesError?.errorText}</Alert>}
        {workshopProductsError?.failed && <Alert severity="error">{workshopProductsError?.errorText}</Alert>}
        {[<PageSection>{[(userIsAdmin) ? <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{'Last name, first name'}</TableCell>
                            <TableCell>{'Company'}</TableCell>
                            <TableCell>{'Confirmed'}</TableCell>
                            <TableCell>{'Registered'}</TableCell>
                            <TableCell>{''}</TableCell>
                        </TableRow>
                    </TableHead>

                    { workshopUsers.length > 0 &&<TableBody className='alternatingBody'>
                    {workshopUsers.map((tableRow, index) => <UserTableRow workshopUser={tableRow} index={index}
                                                                                workshopData={workshopData}
                                                                                products={products}
                                                                                workshopProducts={workshopProducts}
                                                                                setWorkshopProducts={setWorkshopProducts}
                                                                                interfaces={interfaces}
                                                                                productInterfaces={productInterfaces}
                                                                                setProductInterfaces={setProductInterfaces}/>) }
                    </TableBody>}
                
            </Table>
        </TableContainer> : (singleUser) ? <WsProductInterfaceTable workshopData={workshopData} workshopUser={singleUser} interfaces={interfaces} productInterfaces={productInterfaces} products={products}
            setProductInterfaces={setProductInterfaces} workshopProducts={workshopProducts} setWorkshopProducts={setWorkshopProducts}  />
            : "You are not a user in this workshop"]}
            </PageSection>
        ]}</Page>
}